import React, { useState, useEffect } from 'react';
import { FaArrowLeft, FaChalkboardTeacher, FaUsers } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { useNavigation } from '../contexts/NavigationContext';
import api from '../api';
import PageLayout from '../components/PageLayout';

const CreateClassPage = () => {
    const { user } = useAuth();
    const { navigateTo } = useNavigation();
    
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [schedule, setSchedule] = useState('');
    const [teacherId, setTeacherId] = useState(user.id);
    const [availableTeachers, setAvailableTeachers] = useState([]);
    const [loading, setLoading] = useState(false);

    const isMaster = user && (user.role === 'master' || user.username === 'theworldplex');

    useEffect(() => {
        // Only Master needs to fetch teachers to assign them
        if (isMaster) {
            const fetchTeachers = async () => {
                try {
                    const res = await api.get('/users');
                    // FIX: Allow Master to be selected as a teacher too!
                    const teachers = res.data.filter(u => 
                        u.role === 'teacher' || 
                        u.role === 'master' || 
                        u.username === 'theworldplex'
                    );
                    setAvailableTeachers(teachers);
                } catch (e) {
                    console.error("Failed to load teachers", e);
                }
            };
            fetchTeachers();
        }
    }, [isMaster]);

    const handleCreate = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post('/classes', {
                name,
                description,
                teacherId: isMaster ? teacherId : user.id, // Teachers auto-assign themselves
                schedule
            });
            alert("Class created successfully!");
            navigateTo('dashboard');
        } catch (error) {
            alert("Failed to create class.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <PageLayout>
            <div className="max-w-2xl mx-auto pt-10">
                <button onClick={() => navigateTo('dashboard')} className="flex items-center gap-2 text-gray-500 hover:text-blue-600 mb-6 font-bold">
                    <FaArrowLeft /> Back to Dashboard
                </button>

                <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100">
                    <h1 className="text-3xl font-black text-gray-800 mb-6 flex items-center gap-3">
                        <FaChalkboardTeacher className="text-indigo-600"/> Create New Class
                    </h1>

                    <form onSubmit={handleCreate} className="space-y-6">
                        {/* Class Name */}
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-2">Class Name</label>
                            <input 
                                type="text" 
                                required
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
                                placeholder="e.g. Advanced English Grammar"
                            />
                        </div>

                        {/* Description */}
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-2">Description</label>
                            <textarea 
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none h-32 resize-none"
                                placeholder="What will be taught?"
                            />
                        </div>

                        {/* MASTER ONLY: Select Teacher */}
                        {isMaster && (
                            <div className="bg-indigo-50 p-4 rounded-xl border border-indigo-100">
                                <label className="block text-sm font-bold text-indigo-900 mb-2 flex items-center gap-2">
                                    <FaUsers /> Assign Teacher
                                </label>
                                <select 
                                    value={teacherId}
                                    onChange={(e) => setTeacherId(e.target.value)}
                                    className="w-full p-3 border border-indigo-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none bg-white font-bold text-gray-700"
                                >
                                    <option value={user.id}>Myself (Master)</option>
                                    {availableTeachers.map(t => (
                                        <option key={t.id} value={t.id}>
                                            {t.name} ({t.role})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Schedule (Optional Text) */}
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-2">Schedule / Time</label>
                            <input 
                                type="text" 
                                value={schedule}
                                onChange={(e) => setSchedule(e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
                                placeholder="e.g. Mondays at 10:00 AM"
                            />
                        </div>

                        <button 
                            type="submit" 
                            disabled={loading}
                            className="w-full bg-indigo-600 text-white font-bold py-4 rounded-xl shadow-lg hover:bg-indigo-700 transition transform active:scale-95"
                        >
                            {loading ? "Creating..." : "Create Class"}
                        </button>
                    </form>
                </div>
            </div>
        </PageLayout>
    );
};

export default CreateClassPage;